import type { NavGroupId } from "@/widgets/app-sidebar/sidebarNav";

export type BreadcrumbParent = { label: string; href: string };

export type BreadcrumbRouteDef = {
  pattern: string;
  label: string;
  sectionGroupId?: NavGroupId;
  parent?: BreadcrumbParent;
  dynamicLabel?: boolean;
  /** When true, the layout breadcrumb bar is omitted (page title is enough). */
  hideBreadcrumb?: boolean;
};

/** More specific patterns first. */
export const BREADCRUMB_ROUTES: BreadcrumbRouteDef[] = [
  {
    pattern: "/donors/:kind/:id/notes",
    label: "Notes",
    sectionGroupId: "finance",
    parent: { label: "Donors", href: "/donors" },
    dynamicLabel: false,
  },
  {
    pattern: "/donors/:kind/:id",
    label: "Donor",
    sectionGroupId: "finance",
    parent: { label: "Donors", href: "/donors" },
    dynamicLabel: true,
  },
  { pattern: "/donors", label: "Donors", sectionGroupId: "finance" },
  {
    pattern: "/people/:id/timeline",
    label: "Work timeline",
    sectionGroupId: "services",
    parent: { label: "Person", href: "/people/:id" },
    dynamicLabel: false,
  },
  {
    pattern: "/people/:id",
    label: "Person",
    sectionGroupId: "services",
    parent: { label: "Community", href: "/community" },
    dynamicLabel: true,
  },
  { pattern: "/community", label: "Community", sectionGroupId: "services" },
  {
    pattern: "/cases/:id/referral-brief",
    label: "Referral brief",
    sectionGroupId: "services",
    parent: { label: "Cases", href: "/cases" },
  },
  {
    pattern: "/cases/:id",
    label: "Case",
    sectionGroupId: "services",
    parent: { label: "Cases", href: "/cases" },
    dynamicLabel: true,
  },
  { pattern: "/cases", label: "Cases", sectionGroupId: "services" },
  { pattern: "/tasks", label: "Tasks", sectionGroupId: "services" },
  {
    pattern: "/scheduling/events/:id",
    label: "Event",
    sectionGroupId: "scheduling",
    parent: { label: "Scheduling", href: "/scheduling" },
    dynamicLabel: true,
  },
  { pattern: "/scheduling", label: "Scheduling", sectionGroupId: "scheduling" },
  { pattern: "/facilities", label: "Facilities", sectionGroupId: "scheduling" },
  { pattern: "/events", label: "Events", sectionGroupId: "scheduling" },
  {
    pattern: "/check-in/people/:personId",
    label: "Work session",
    sectionGroupId: "services",
    parent: { label: "Check-In", href: "/check-in" },
    dynamicLabel: false,
  },
  { pattern: "/check-in", label: "Check-In", sectionGroupId: "services" },
  {
    pattern: "/inventory/items/:id",
    label: "Item",
    sectionGroupId: "services",
    parent: { label: "Inventory", href: "/inventory" },
    dynamicLabel: true,
  },
  { pattern: "/inventory", label: "Inventory", sectionGroupId: "services" },
  { pattern: "/programs", label: "Departments", sectionGroupId: "scheduling" },
  { pattern: "/assistance-programs", label: "Programs", sectionGroupId: "services" },
  { pattern: "/time-tracker", label: "Time Tracker", sectionGroupId: "services" },
  {
    pattern: "/resource-directory/:id/notes",
    label: "Notes",
    sectionGroupId: "services",
    parent: { label: "Resource", href: "/resource-directory/:id" },
    dynamicLabel: true,
  },
  {
    pattern: "/resource-directory/:id",
    label: "Resource",
    sectionGroupId: "services",
    parent: { label: "Resource directory", href: "/resource-directory" },
    dynamicLabel: true,
  },
  { pattern: "/resource-directory", label: "Resource directory", sectionGroupId: "services" },
  { pattern: "/vendors", label: "Vendors", sectionGroupId: "finance" },
  {
    pattern: "/grants/management/funds/:id",
    label: "Fund",
    sectionGroupId: "finance",
    parent: { label: "Grant management", href: "/grants/management?tab=funds" },
    dynamicLabel: true,
  },
  {
    pattern: "/grants/management",
    label: "Grant management",
    sectionGroupId: "finance",
    parent: { label: "Grants", href: "/grants" },
  },
  {
    pattern: "/accounting/funds/:id",
    label: "Fund",
    sectionGroupId: "finance",
    parent: { label: "Grant management", href: "/grants/management?tab=funds" },
    dynamicLabel: true,
  },
  { pattern: "/accounting", label: "Accounting", sectionGroupId: "finance" },
  {
    pattern: "/submission-center",
    label: "Submission Center",
    sectionGroupId: "finance",
    hideBreadcrumb: true,
  },
  { pattern: "/giving-statements", label: "Giving Statements", sectionGroupId: "finance" },
  { pattern: "/reports", label: "Reports", sectionGroupId: "finance" },
  {
    pattern: "/grants/funders/:id",
    label: "Funder",
    sectionGroupId: "finance",
    parent: { label: "Grants", href: "/grants" },
    dynamicLabel: true,
  },
  {
    pattern: "/grants/:id",
    label: "Grant",
    sectionGroupId: "finance",
    parent: { label: "Grants", href: "/grants" },
    dynamicLabel: true,
  },
  { pattern: "/grants", label: "Grants", sectionGroupId: "finance" },
  {
    pattern: "/settings/team-permissions",
    label: "Team permissions",
    sectionGroupId: "management",
    parent: { label: "Settings", href: "/settings?section=permissions" },
  },
  { pattern: "/settings", label: "Settings", sectionGroupId: "management" },
  {
    pattern: "/activity",
    label: "Recent Activity",
    sectionGroupId: "management",
    parent: { label: "Settings", href: "/settings?section=activity" },
  },
  {
    pattern: "/data",
    label: "Data Management",
    sectionGroupId: "management",
    parent: { label: "Settings", href: "/settings?section=data" },
  },
  {
    pattern: "/billing",
    label: "Billing",
    sectionGroupId: "management",
    parent: { label: "Settings", href: "/settings?section=billing" },
  },
  { pattern: "/board-report", label: "Board Report", sectionGroupId: "management" },
  {
    pattern: "/copilot/playbooks/:id",
    label: "Playbook",
    sectionGroupId: "management",
    parent: { label: "Copilot workflows", href: "/copilot/playbooks" },
    dynamicLabel: true,
  },
  { pattern: "/copilot/playbooks", label: "Copilot workflows", sectionGroupId: "management" },
  { pattern: "/team/invitations", label: "Team invitations", sectionGroupId: "management" },
  {
    pattern: "/control/applications/:id",
    label: "Application",
    sectionGroupId: "platform",
    parent: { label: "Applications", href: "/control/applications" },
    dynamicLabel: true,
  },
  { pattern: "/control/applications", label: "Applications", sectionGroupId: "platform" },
  {
    pattern: "/control/kpis",
    label: "KPIs",
    sectionGroupId: "platform",
    parent: { label: "Control", href: "/control" },
  },
  {
    pattern: "/control/analytics",
    label: "Analytics",
    sectionGroupId: "platform",
    parent: { label: "Control", href: "/control" },
  },
  {
    pattern: "/control/announcements",
    label: "Announcements",
    sectionGroupId: "platform",
    parent: { label: "Control", href: "/control" },
  },
  {
    pattern: "/control/billing",
    label: "Billing admin",
    sectionGroupId: "platform",
    parent: { label: "Control", href: "/control" },
  },
  { pattern: "/control", label: "Control", sectionGroupId: "platform" },
  { pattern: "/", label: "Dashboard" },
];

export type MatchedRoute = {
  def: BreadcrumbRouteDef;
  params: Record<string, string>;
};

export function matchBreadcrumbRoute(pathname: string): MatchedRoute | null {
  const path = pathname.replace(/\/$/, "") || "/";
  for (const def of BREADCRUMB_ROUTES) {
    const normalized = def.pattern.replace(/\/$/, "") || "/";
    if (normalized === "/") {
      if (path === "/" || path === "") return { def, params: {} };
      continue;
    }
    const paramNames: string[] = [];
    const parts = normalized.split("/").filter(Boolean);
    const reParts = parts.map((part) => {
      if (part.startsWith(":")) {
        paramNames.push(part.slice(1));
        return "([^/]+)";
      }
      return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    });
    const re = new RegExp(`^/${reParts.join("/")}/?$`);
    const m = path.match(re);
    if (!m) continue;
    const params: Record<string, string> = {};
    paramNames.forEach((name, i) => {
      params[name] = m[i + 1] ?? "";
    });
    return { def, params };
  }
  return null;
}

export function foldCrumbs<T>(
  items: T[],
  maxVisible: number,
): { head: T[]; tail: T[]; folded: T[] } {
  if (items.length <= maxVisible) {
    return { head: items, tail: [], folded: [] };
  }
  const tailCount = 2;
  const headCount = 1;
  return {
    head: items.slice(0, headCount),
    folded: items.slice(headCount, items.length - tailCount),
    tail: items.slice(items.length - tailCount),
  };
}
